import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

interface PrivacyPolicyPageProps {
  onBack: () => void;
}

export const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen relative">
      {/* Back Button */}
      <div className="absolute top-8 left-8 z-20">
        <button
          onClick={onBack}
          className="inline-flex items-center space-x-2 text-white hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Quellect</span>
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card p-8 sm:p-12"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              >
                Privacy Policy
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-lg text-gray-600"
              >
                for Quellect.ai
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-4 text-sm text-gray-500 space-y-1"
              >
                <p>Effective date: August 31, 2025</p>
                <p>Contact: info@quellect.ai</p>
              </motion.div>
            </div>

            {/* Privacy Policy Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="prose prose-lg max-w-none text-gray-700"
            >
              <div className="space-y-8">
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
                  <p className="mb-3">
                    When you join the Quellect waitlist, we may collect:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Email address (required)</li>
                    <li>Optional responses you provide (e.g. topics you'd Quellect, platforms you want supported, role, use case, etc.)</li>
                    <li>Technical details such as browser user-agent, IP address, and referral source (used for analytics and abuse prevention).</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
                  <p className="mb-3">We use this data to:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Manage the waitlist and confirm your signup (double opt-in).</li>
                    <li>Provide early access and updates about Quellect.</li>
                    <li>Analyze aggregated data to improve our product (e.g. feature prioritization).</li>
                  </ul>
                  <p className="mt-3">
                    We do not sell or rent your data to third parties.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Sharing of Information</h2>
                  <p className="mb-3">
                    We may share information only with trusted service providers who help us operate the waitlist, such as:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Supabase (database hosting)</li>
                    <li>Resend (transactional email provider)</li>
                    <li>hCaptcha (bot protection)</li>
                  </ul>
                  <p className="mt-3">
                    These providers process data on our behalf under appropriate safeguards.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Your Rights</h2>
                  <p className="mb-3">You may at any time:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Unsubscribe from emails (link provided in communications).</li>
                    <li>Request deletion of your data by contacting us at info@quellect.ai.</li>
                  </ul>
                  <p className="mt-3">
                    If you are in the EU/EEA, you have rights under GDPR (access, rectification, erasure, objection). 
                    If you are in California, you may have rights under CCPA.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Security</h2>
                  <p>
                    We take reasonable measures to protect your data, but no system is 100% secure. 
                    Please use caution when sharing personal information online.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Changes to This Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. If changes are material, 
                    we'll notify you by updating the "Effective Date" above.
                  </p>
                </section>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
